"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.lSetAspectRatio = lSetAspectRatio;
exports.logicalWithFallback = logicalWithFallback;

function lSetAspectRatio(width, height) {
  return `
    &::before {
      float: left;
      content: "";
      padding-top: ${height / width * 100}%;
    }

    &::after {
      display: block;
      content: "";
      clear: both;
    }

    @supports (aspect-ratio: 1 / 1) {
      aspect-ratio: ${width} / ${height};

      &::before,
      &::after {
        content: none;
      }
    }
  `;
}

const LOGICAL_FALLBACK_MAP = {
  "block-size": "height",
  "max-block-size": "max-height",
  "min-block-size": "min-height",
  "inline-size": "width",
  "max-inline-size": "max-width",
  "min-inline-size": "min-width",
  "margin-inline": ["margin-left", "margin-right"],
  "margin-inline-end": {
    ltr: "margin-right",
    rtl: "margin-left"
  },
  "margin-inline-start": {
    ltr: "margin-left",
    rtl: "margin-right"
  },
  "margin-block": ["margin-top", "margin-bottom"],
  "margin-block-end": "margin-bottom",
  "margin-block-start": "margin-top",
  inset: ["top", "right", "bottom", "left"],
  "inset-inline": ["left", "right"],
  "inset-inline-end": {
    ltr: "right",
    rtl: "left"
  },
  "inset-inline-start": {
    ltr: "left",
    rtl: "right"
  },
  "inset-block": ["top", "bottom"],
  "inset-block-end": "bottom",
  "inset-block-start": "top",
  "padding-inline": ["padding-left", "padding-right"],
  "padding-inline-end": {
    ltr: "padding-right",
    rtl: "padding-left"
  },
  "padding-inline-start": {
    ltr: "padding-left",
    rtl: "padding-right"
  },
  "padding-block": ["padding-top", "padding-bottom"],
  "padding-block-end": "padding-bottom",
  "padding-block-start": "padding-top",
  "border-block": ["border-top", "border-bottom"],
  "border-block-color": ["border-top-color", "border-bottom-color"],
  "border-block-style": ["border-top-style", "border-bottom-style"],
  "border-block-width": ["border-top-width", "border-bottom-width"],
  "border-block-end": "border-bottom",
  "border-block-end-color": "border-bottom-color",
  "border-block-end-style": "border-bottom-style",
  "border-block-end-width": "border-bottom-width",
  "border-block-start": "border-top",
  "border-block-start-color": "border-top-color",
  "border-block-start-style": "border-top-style",
  "border-block-start-width": "border-top-width",
  "border-inline": ["border-left", "border-right"],
  "border-inline-color": ["border-left-color", "border-right-color"],
  "border-inline-style": ["border-left-style", "border-right-style"],
  "border-inline-width": ["border-left-width", "border-right-width"],
  "border-inline-end": {
    ltr: "border-right",
    rtl: "border-left"
  },
  "border-inline-end-color": {
    ltr: "border-right-color",
    rtl: "border-left-color"
  },
  "border-inline-end-style": {
    ltr: "border-right-style",
    rtl: "border-left-style"
  },
  "border-inline-end-width": {
    ltr: "border-right-width",
    rtl: "border-left-width"
  },
  "border-inline-start": {
    ltr: "border-left",
    rtl: "border-right"
  },
  "border-inline-start-color": {
    ltr: "border-left-color",
    rtl: "border-right-color"
  },
  "border-inline-start-style": {
    ltr: "border-left-style",
    rtl: "border-right-style"
  },
  "border-inline-start-width": {
    ltr: "border-left-width",
    rtl: "border-right-width"
  },
  "border-end-end-radius": {
    ltr: "border-bottom-right-radius",
    rtl: "border-bottom-left-radius"
  },
  "border-end-start-radius": {
    ltr: "border-bottom-left-radius",
    rtl: "border-bottom-right-radius"
  },
  "border-start-end-radius": {
    ltr: "border-top-right-radius",
    rtl: "border-top-left-radius"
  },
  "border-start-start-radius": {
    ltr: "border-top-left-radius",
    rtl: "border-top-right-radius"
  }
};

function generateFallbackDeclaration(property, value) {
  const fallbackProperty = LOGICAL_FALLBACK_MAP[property];
  if (!fallbackProperty) return `${property}: ${value};`;
  if (Array.isArray(fallbackProperty)) return fallbackProperty.map(f => `${f}: ${value};`).join("");
  if (typeof fallbackProperty === "object" && "rtl" in fallbackProperty) return `
      [dir='ltr'] & { ${fallbackProperty.ltr}: ${value}; }
      [dir='rtl'] & { ${fallbackProperty.rtl}: ${value}; }
  `;
  return `${fallbackProperty}: ${value};`;
}

function transformLogicalProperty(property, value) {
  const logicalDeclaration = `${property}: ${value};`;
  const fallbackDeclaration = generateFallbackDeclaration(property, value);
  if (!fallbackDeclaration) return logicalDeclaration;
  return `
    ${logicalDeclaration}

    @supports not ((${logicalDeclaration.replace(";", "")})) {
      ${fallbackDeclaration}
    }
  `;
}
/**
 * Create logical property declarations with fallbacks to
 * directional properties wrapped in a `@supports not` test
 * @param declarations {object} Key/value pairs representing CSS declarations (e.g. { "margin-block-start": "20px" })
 * @returns {string}
 */


function logicalWithFallback(declarations = {}) {
  const transformedDeclarations = Object.keys(declarations).map(property => transformLogicalProperty(property, declarations[property]));
  const output = transformedDeclarations.join("");
  return output;
}